// Unpublished Work © 2025 Deere & Company.
import React from 'react';
import {connect} from 'react-redux';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import PropTypes from 'Utils/prop-type-utils';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import {Box, Button, Stack, DropdownMenu} from '@deere/isg.component-library';
import {withRouter} from 'react-router-dom';
import {WORKBOARD_TYPES} from '../constants/workboard-creation-flows';
import dialogTypes from 'Ui/components/common/dialog-types';
import {resetState, setCopyWorkboardMode, setIsSaving, setLoadingSetup, setWorkItems, setNameValidation, updateDetailsField} from 'Src/ui-core/store/actions/setup-workboard';
import useWorkboardWizardActions from './hooks/use-workboard-wizard-actions';

const DROPDOWN_STYLE = {
    width: '100% !important',
    '& .MuiButtonBase-root': {
        width: '100%',
        justifyContent: 'center !important',
        '& .MuiButton-icon': {
            marginLeft: 'unset'
        }
    }
};

const ACTION_TYPES = {
    NEXT: 'NEXT',
    BACK: 'BACK',
    CANCEL: 'CANCEL',
    DELETE: 'DELETE'
};

function getDesktopButtons(type, otherActionItems, handleSaveAsDraft, handlePreview, handleNext, handlePublish,
    handlePublishTemplate, activeStep, isDisabled, isLoading, showSaveDraftBtn, translations) {
    if (WORKBOARD_TYPES.includes(type)) {
        return (
            <Stack
                direction={'row'}
                spacing={2}
            >
                <DropdownMenu
                    buttonTitle={translations.ONLINK_OTHER_ACTIONS}
                    items={otherActionItems}
                    loading={isLoading}
                />
                {
                    showSaveDraftBtn &&
                    <Button
                        disabled={isDisabled}
                        loading={isLoading}
                        onClick={() => handleSaveAsDraft()}
                    >
                        {translations.SAVE_DRAFT}
                    </Button>
                }
                <Button
                    disabled={isDisabled}
                    loading={isLoading}
                    onClick={handlePreview}
                >
                    {translations.PREVIEW}
                </Button>
                <Button
                    disabled={isDisabled}
                    loading={isLoading}
                    onClick={activeStep === 0 ? handleNext : handlePublish}
                    variant='primary'
                >
                    {activeStep === 0 ? translations.NEXT : translations.ONLINK_PUBLISH}
                </Button>
            </Stack>
        );
    }
    return (
        <Stack
            direction={'row'}
            spacing={2}
        >
            {
                activeStep === 0 &&
                <Button
                    disabled={isDisabled}
                    loading={isLoading}
                    onClick={handlePublishTemplate}
                >
                    {translations.ONLINK_SAVE_TEMPLATE}
                </Button>
            }
            <Button
                disabled={isDisabled}
                loading={isLoading}
                onClick={activeStep === 0 ? handleNext : handlePublishTemplate}
                variant='primary'
            >
                {activeStep === 0 ? translations.NEXT : translations.ONLINK_SAVE_TEMPLATE}
            </Button>
        </Stack>
    );
}

function getMobileWorkboardButtons(dropdownItems, handleNext, handlePublish, activeStep, isDisabled, isLoading, translations) {
    return (
        <Stack
            direction={'row'}
            justifyContent={'space-between'}
            spacing={'1rem'}
            width={'100%'}
        >
            <DropdownMenu
                buttonTitle={translations.ACTIONS}
                flexGrow={true}
                items={dropdownItems}
                loading={isLoading}
                sx={DROPDOWN_STYLE}
            />
            <Button
                disabled={isDisabled}
                fullWidth={true}
                loading={isLoading}
                onClick={activeStep === 0 ? handleNext : handlePublish}
                variant='primary'
            >
                {activeStep === 0 ? translations.NEXT : translations.ONLINK_PUBLISH}
            </Button>
        </Stack>
    );
}

function getMobileTemplateButtons(handleCancel, handleBack, handleNext, handlePublishTemplate, activeStep, isDisabled, isLoading, translations, templateButtonsDisabled) {
    return (
        <Stack
            spacing={'1rem'}
            width={'100%'}
        >
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={'1rem'}
                width={'100%'}
            >
                <Button
                    fullWidth={true}
                    loading={isLoading}
                    onClick={handleCancel}
                >
                    {translations.CANCEL}
                </Button>
                <Button
                    disabled={templateButtonsDisabled || isDisabled && activeStep === 0}
                    fullWidth={true}
                    loading={isLoading}
                    onClick={activeStep === 0 ? handlePublishTemplate : handleBack}
                >
                    {activeStep === 0 ? translations.ONLINK_SAVE_TEMPLATE : translations.ONLINK_BACK_TO_SETUP}
                </Button>
            </Stack>
            <Button
                disabled={templateButtonsDisabled || isDisabled}
                fullWidth={true}
                loading={isLoading}
                onClick={activeStep === 0 ? handleNext : handlePublishTemplate}
                variant='primary'
            >
                {activeStep === 0 ? translations.NEXT : translations.ONLINK_SAVE_TEMPLATE}
            </Button>
        </Stack>
    );
}

function WorkboardActionButtons(props) {
    const {
        activeStep,
        creationFlow,
        handleLoadingSetup,
        handleDetailsField,
        handleNameValidation,
        handleIsSaving,
        handleSetCopyWorkboardMode,
        handleSetWorkItems,
        history,
        initialWorkboard,
        isgToggles,
        isMobile,
        membership,
        openConfirmation,
        closeConfirmation,
        openWorkboardPreviewDialog,
        operators,
        resetSetupState,
        rowOrder,
        setActiveStep,
        stepsLength,
        templates,
        translations,
        type,
        workboardData
    } = props;

    const {
        handleAction,
        handleCopy,
        handlePreview,
        handlePublish,
        handlePublishTemplate,
        handleSaveAsDraft,
        handlePrintWorkboard,
        isDisabled,
        isLoading,
        isTemplateDuplicateActive,
        showSaveDraftBtn
    } = useWorkboardWizardActions({
        activeStep,
        creationFlow,
        handleLoadingSetup,
        handleDetailsField,
        handleNameValidation,
        handleIsSaving,
        handleSetCopyWorkboardMode,
        handleSetWorkItems,
        history,
        isgToggles,
        isMobile,
        membership,
        openConfirmation,
        closeConfirmation,
        openWorkboardPreviewDialog,
        operators,
        resetSetupState,
        rowOrder,
        setActiveStep,
        stepsLength,
        templates,
        type,
        translations,
        workboardData
    });

    const otherActionItems = React.useMemo(() => {
        return [
            workboardData.isEditing && {
                title: translations.PRINT,
                disabled: isDisabled,
                loading: isLoading,
                onClick: handlePrintWorkboard
            },
            {
                title: translations.COPY,
                disabled: isDisabled,
                loading: isLoading,
                onClick: handleCopy
            },
            workboardData.isEditing && {
                title: translations.DELETE,
                loading: isLoading,
                onClick: () => handleAction(ACTION_TYPES.DELETE)
            }
        ].filter(Boolean);
    }, [handleAction, handleCopy, handlePrintWorkboard, isDisabled, isLoading, translations.PRINT, translations.COPY, translations.DELETE, workboardData.isEditing]);

    const dropdownItems = React.useMemo(() => {
        return [
            {
                title: translations.CANCEL,
                onClick: () => handleAction(ACTION_TYPES.CANCEL, initialWorkboard, {
                    ...workboardData,
                    rowOrder
                })
            },
            activeStep > 0 && {
                title: translations.ONLINK_BACK_TO_SETUP,
                onClick: () => handleAction(ACTION_TYPES.BACK)
            },
            ...otherActionItems,
            showSaveDraftBtn && {
                title: translations.SAVE_DRAFT,
                disabled: isDisabled,
                loading: isLoading,
                onClick: handleSaveAsDraft
            },
            {
                title: translations.PREVIEW,
                disabled: isDisabled,
                loading: isLoading,
                onClick: handlePreview
            }
        ].filter(Boolean);
    }, [activeStep, handleAction, handlePreview, handleSaveAsDraft, isDisabled, isLoading, otherActionItems, showSaveDraftBtn, translations.CANCEL, translations.ONLINK_BACK_TO_SETUP, translations.SAVE_DRAFT, translations.PREVIEW]);

    return (
        <Box
            alignItems={'center'}
            bottom={0}
            display={'flex'}
            justifyContent={'space-between'}
            p={'1rem'}
            position={'sticky'}
            zIndex={10}
        >
            {!isMobile &&
                <>
                    <Stack
                        direction={'row'}
                        spacing={'1rem'}
                    >
                        <Button
                            onClick={() => handleAction(ACTION_TYPES.CANCEL, initialWorkboard, {
                                ...workboardData,
                                rowOrder
                            })}
                        >
                            {translations.CANCEL}
                        </Button>
                        <Button
                            disabled={isTemplateDuplicateActive}
                            onClick={() => handleAction(ACTION_TYPES.BACK)}
                            sx={{
                                display: activeStep === 0 ? 'none' : ''
                            }}
                        >
                            {translations.ONLINK_BACK_TO_SETUP}
                        </Button>
                    </Stack>
                    {getDesktopButtons(type, otherActionItems, handleSaveAsDraft, handlePreview, () => handleAction(ACTION_TYPES.NEXT), handlePublish, handlePublishTemplate, activeStep, isDisabled, isLoading, showSaveDraftBtn, translations)}
                </>
            }

            {isMobile && WORKBOARD_TYPES.includes(type) &&
                getMobileWorkboardButtons(
                    dropdownItems,
                    () => handleAction(ACTION_TYPES.NEXT),
                    handlePublish,
                    activeStep,
                    isDisabled,
                    isLoading,
                    translations
                )
            }

            {isMobile && !WORKBOARD_TYPES.includes(type) &&
                getMobileTemplateButtons(
                    () => handleAction(ACTION_TYPES.CANCEL, initialWorkboard, {
                        ...workboardData,
                        rowOrder
                    }),
                    () => handleAction(ACTION_TYPES.BACK),
                    () => handleAction(ACTION_TYPES.NEXT),
                    handlePublishTemplate,
                    activeStep,
                    isDisabled,
                    isLoading,
                    translations,
                    isTemplateDuplicateActive
                )
            }
        </Box>
    );
}

WorkboardActionButtons.propTypes = {
    activeStep: PropTypes.number,
    closeConfirmation: PropTypes.func,
    creationFlow: PropTypes.string,
    handleDetailsField: PropTypes.func,
    handleIsSaving: PropTypes.func,
    handleLoadingSetup: PropTypes.func,
    handleNameValidation: PropTypes.func,
    handleSetCopyWorkboardMode: PropTypes.func,
    handleSetWorkItems: PropTypes.func,
    history: PropTypes.history,
    initialWorkboard: PropTypes.object,
    isEdit: PropTypes.bool,
    isgToggles: PropTypes.object,
    isMobile: PropTypes.bool,
    membership: PropTypes.membership,
    openConfirmation: PropTypes.func,
    openWorkboardPreviewDialog: PropTypes.func,
    operators: PropTypes.arrayOf(PropTypes.object),
    resetSetupState: PropTypes.func,
    rowOrder: PropTypes.array,
    setActiveStep: PropTypes.func,
    stepsLength: PropTypes.number,
    templates: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations,
    type: PropTypes.string,
    workboardData: PropTypes.object
};

export function mapStateToProps(state) {
    return {
        isgToggles: state.account.isgToggles,
        membership: state.membership,
        operators: state.setupWorkboard.operators.teamMembers,
        rowOrder: state.setupWorkboard.rowOrder,
        creationFlow: state.setupWorkboard.creationFlow,
        templates: state.setupWorkboard.jobs.jobs,
        workboardData: {
            details: state.setupWorkboard.details,
            operators: state.setupWorkboard.operators.selected,
            jobs: state.setupWorkboard.jobs.selectedJobs,
            cutsAndDirections: state.setupWorkboard.cutsAndDirections.selectedMowingDirections,
            workItems: state.setupWorkboard.workItems,
            isEditing: state.setupWorkboard.isEditing,
            isDraft: state.setupWorkboard.isDraft,
            workboardId: state.setupWorkboard.workboardId,
            nameValidation: state.setupWorkboard.nameValidation,
            initialSelectedOperators: state.setupWorkboard.initialSelectedOperators,
            initialSelectedJobs: state.setupWorkboard.initialSelectedJobs,
            initialWorkItems: state.setupWorkboard.initialWorkItems
        },
        initialWorkboard: state.setupWorkboard.initialWorkboard
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        handleLoadingSetup(props) {
            dispatch(setLoadingSetup(props));
        },
        handleDetailsField(props) {
            dispatch(updateDetailsField(props));
        },
        handleNameValidation(value) {
            dispatch(setNameValidation(value));
        },
        handleIsSaving(props) {
            dispatch(setIsSaving(props));
        },
        handleSetCopyWorkboardMode() {
            dispatch(setCopyWorkboardMode());
        },
        handleSetWorkItems(props) {
            dispatch(setWorkItems(props));
        },
        openWorkboardPreviewDialog(props) {
            dispatch(openDialog(dialogTypes.WORKBOARD_PREVIEW_DIALOG, props));
        },
        resetSetupState() {
            dispatch(resetState());
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MediaQuery(MOBILE_MEDIA_QUERY)(WorkboardActionButtons)));
